import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { AppSettings } from '@toiyp/shared';
import { API_ORIGIN } from '../../config';

export interface AdminOverview {
  users: number;
  signups7d: number;
  signups30d: number;
  active7d: number;
  pendingInvitees: number;
  pushEnabledUsers: number;
  activeRelationships: number;
  pendingRelationships: number;
  lettersSent: number;
  lettersSent7d: number;
  lettersOpened: number;
  lettersInFlight: number;
}

/** Counts only — the API never sends letter contents or who writes to whom. */
export interface AdminUserRow {
  id: string;
  email: string;
  name: string;
  handle: string | null;
  signedUpAt: string;
  lastActiveAt: string | null;
  lastWroteAt: string | null;
  relationships: number;
  lettersWritten: number;
  lettersReceived: number;
  lettersOpened: number;
  reads: number;
  unopened: number;
  pushEnabled: boolean;
  /** toiyp Plus until this date (admin-granted for now); null when not Plus. */
  plusUntil: string | null;
}

/** Custom stamps (toiyp Plus): a collection as the admin sees it, retired designs included. */
export interface AdminStampDesign {
  id: string;
  name: string;
  imageUrl: string;
  width: number | null;
  height: number | null;
  active: boolean;
  sort: number;
}

/** A letter paper as the admin sees it, retired ones included. */
export interface AdminStationery {
  id: string;
  kind: 'paper' | 'envelope';
  name: string;
  imageUrl: string;
  width: number;
  height: number;
  area: { top: number; right: number; bottom: number; left: number };
  ink: string;
  scale: number;
  active: boolean;
  sort: number;
}

export interface NewStationery {
  kind?: 'paper' | 'envelope';
  scale?: number;
  name: string;
  data: string;
  mime: string;
  width: number;
  height: number;
  /** Paper only. */
  area?: { top: number; right: number; bottom: number; left: number };
  ink?: string;
}

export interface AdminStampCollection {
  id: string;
  name: string;
  description: string;
  active: boolean;
  plusOnly: boolean;
  sort: number;
  owners: number;
  designs: AdminStampDesign[];
  papers: AdminStationery[];
  envelopes: AdminStationery[];
}

/** Admin grants only — member-to-member gifts are never listed. */
export interface AdminStampGrant {
  userId: string;
  email: string;
  handle: string | null;
  collectionId: string;
  collectionName: string;
  createdAt: string;
}

export interface NewStampDesign {
  name: string;
  data: string;
  mime: string;
  width?: number;
  height?: number;
}

/** Postage usage — class counts and stamp totals per member, never which pair. */
export interface AdminPostage {
  letters: Record<'second' | 'first' | 'express', { all: number; last30d: number }>;
  stamps: { earned: number; spent: number; refunded: number; outstanding: number };
  members: {
    id: string;
    name: string;
    handle: string | null;
    earned: number;
    spent: number;
    refunded: number;
    balance: number;
    lastActivityAt: string | null;
  }[];
}

/** Post Office (Phase 4): feature introductions sent vs first opened, from the ledger. */
export interface AdminAnnouncements {
  totals: { sent: number; opened: number };
  features: {
    key: string;
    channel: 'letter' | 'notice';
    sent: number;
    opened: number;
    lastSentAt: string | null;
  }[];
}

export interface AdminSettingsView {
  values: AppSettings;
  defaults: AppSettings;
  updatedAt: string | null;
  updatedBy: string | null;
}

/** The Referrals view (slice 4). */
export interface AdminReferrals {
  byVia: { via: string; count: number }[];
  topInviters: { handle: string | null; displayName: string | null; referred: number; rewarded: number }[];
  referredTotal: number;
  rewardedTotal: number;
  settings: { lastingLinkDailyCap: number; referralMonthlyRewardLimit: number };
}

export const adminApi = createApi({
  reducerPath: 'adminApi',
  baseQuery: fetchBaseQuery({ baseUrl: `${API_ORIGIN}/api/admin`, credentials: 'include' }),
  tagTypes: ['Settings', 'Users', 'Stamps'],
  endpoints: (b) => ({
    /** Whether to show the Admin entry point. The server enforces it regardless. */
    adminMe: b.query<{ isAdmin: boolean }, void>({ query: () => '/me' }),
    adminOverview: b.query<AdminOverview, void>({ query: () => '/overview' }),
    adminUsers: b.query<AdminUserRow[], void>({ query: () => '/users', providesTags: ['Users'] }),
    adminStamps: b.query<{ collections: AdminStampCollection[]; grants: AdminStampGrant[] }, void>({
      query: () => '/stamps',
      providesTags: ['Stamps'],
    }),
    createStampCollection: b.mutation<
      AdminStampCollection,
      { name: string; description?: string; plusOnly?: boolean }
    >({
      query: (body) => ({ url: '/stamps/collections', method: 'POST', body }),
      invalidatesTags: ['Stamps'],
    }),
    updateStampCollection: b.mutation<
      void,
      { id: string; name?: string; description?: string; plusOnly?: boolean; active?: boolean; sort?: number }
    >({
      query: ({ id, ...body }) => ({ url: `/stamps/collections/${id}`, method: 'PATCH', body }),
      invalidatesTags: ['Stamps'],
    }),
    addStampDesigns: b.mutation<unknown, { collectionId: string; designs: NewStampDesign[] }>({
      query: ({ collectionId, designs }) => ({
        url: `/stamps/collections/${collectionId}/designs`,
        method: 'POST',
        body: { designs },
      }),
      invalidatesTags: ['Stamps'],
    }),
    updateStampDesign: b.mutation<void, { id: string; name?: string; active?: boolean; sort?: number }>({
      query: ({ id, ...body }) => ({ url: `/stamps/designs/${id}`, method: 'PATCH', body }),
      invalidatesTags: ['Stamps'],
    }),
    addStationery: b.mutation<unknown, { collectionId: string; paper: NewStationery }>({
      query: ({ collectionId, paper }) => ({
        url: `/stamps/collections/${collectionId}/stationery`,
        method: 'POST',
        body: paper,
      }),
      invalidatesTags: ['Stamps'],
    }),
    updateStationery: b.mutation<
      void,
      { id: string; name?: string; active?: boolean; area?: NewStationery['area']; ink?: string; scale?: number }
    >({
      query: ({ id, ...body }) => ({ url: `/stamps/stationery/${id}`, method: 'PATCH', body }),
      invalidatesTags: ['Stamps'],
    }),
    grantStampCollection: b.mutation<void, { userId: string; collectionId: string }>({
      query: (body) => ({ url: '/stamps/grants', method: 'POST', body }),
      invalidatesTags: ['Stamps'],
    }),
    revokeStampGrant: b.mutation<void, { userId: string; collectionId: string }>({
      query: ({ userId, collectionId }) => ({
        url: `/stamps/grants/${encodeURIComponent(userId)}/${collectionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Stamps'],
    }),
    setPlus: b.mutation<{ plusUntil: string | null }, { userId: string; plus: boolean }>({
      query: ({ userId, plus }) => ({ url: `/stamps/plus/${encodeURIComponent(userId)}`, method: 'PUT', body: { plus } }),
      invalidatesTags: ['Users'],
    }),
    adminPostage: b.query<AdminPostage, void>({ query: () => '/postage' }),
    adminReferrals: b.query<AdminReferrals, void>({ query: () => '/referrals', providesTags: ['Settings'] }),
    adminAnnouncements: b.query<AdminAnnouncements, void>({ query: () => '/announcements' }),
    adminSettings: b.query<AdminSettingsView, void>({ query: () => '/settings', providesTags: ['Settings'] }),
    saveAdminSettings: b.mutation<AdminSettingsView, Partial<AppSettings>>({
      query: (body) => ({ url: '/settings', method: 'PUT', body }),
      invalidatesTags: ['Settings'],
    }),
  }),
});

export const {
  useAdminMeQuery,
  useAdminReferralsQuery,
  useAdminAnnouncementsQuery,
  useAdminOverviewQuery,
  useAdminUsersQuery,
  useAdminSettingsQuery,
  useAdminPostageQuery,
  useSaveAdminSettingsMutation,
  useAdminStampsQuery,
  useCreateStampCollectionMutation,
  useUpdateStampCollectionMutation,
  useAddStampDesignsMutation,
  useUpdateStampDesignMutation,
  useGrantStampCollectionMutation,
  useAddStationeryMutation,
  useUpdateStationeryMutation,
  useRevokeStampGrantMutation,
  useSetPlusMutation,
} = adminApi;
